import { HttpErrorResponse, HttpEventType, HttpStatusCode } from '@angular/common/http';
import { Component, Input, OnDestroy } from '@angular/core';
import { ScannedDocument, WorklistItemDetailsDto } from '@app/api-module/control-centre-api-client';
import { MessageBoxIcon, MessageBoxButtons } from '@app/messagebox/messagebox.model';
import { MessageBoxService } from '@app/messagebox/services/messagebox.service';
import { Constants } from '@app/models/constants';
import { CommonUtilities } from '@app/shared/common-utilities';
import { FileUploadInfo } from '@app/shared/model/file-upload-info.model';
import { FileUploadInfoErrorEnum } from '@app/shared/model/shared.model';
import { WorklistService } from '@app/worklist/worklist.service';
import { faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { Subscription, throwError, TimeoutError, catchError } from 'rxjs';

@Component({
    selector: 'app-scan-docs',
    templateUrl: './scan-docs.component.html',
    styleUrls: ['./scan-docs.component.scss']
})
export class ScanDocsComponent implements OnDestroy {
    @Input() worklistItem: WorklistItemDetailsDto;

    @Input() editable: boolean;

    private static readonly acceptedFileMimeType = ['image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'image/bmp', 'application/pdf'];
    private static readonly acceptedFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp', '.pdf'];
    public static readonly acceptedFileExtensionsStr = ScanDocsComponent.acceptedFileExtensions.join(', ');
    private static readonly maxFileSize = 10 * 1024 * 1024;
    private static readonly maxConcurrentTask = 5;
    private static readonly formDataFileKey = 'File';
    private static readonly UNABLE_TO_UPLOAD_MESSAGE =
        'Documents uploading failed. Please try again in some time. If the problem persists, please contact Everlight support team';

    private _fileUploadTaskQueue: FileUploadTask[] = []; // all the unprocessed tasks
    private _fileUploadProcessingList: FileUploadTask[] = []; // only the processing tasks

    faPaperclip = faPaperclip;
    ScanDocsComponent = ScanDocsComponent;
    fileUploadInfoList: FileUploadInfo[] = [];

    private readonly subscriptions = new Subscription();

    constructor(private readonly worklistService: WorklistService, private readonly messageBoxService: MessageBoxService) {}

    public getFileName(scannedDocument: ScannedDocument, index: number) {
        let fileName = `${this.worklistItem.accessionNumber}-${index + 1}.jpg`;
        if (scannedDocument.originalFileName) {
            fileName += ` (${scannedDocument.originalFileName})`;
        }
        return fileName;
    }

    onFileSelected(event: Event) {
        const eventTarget = event.target as HTMLInputElement;
        Array.from(eventTarget.files).forEach(file => this.uploadFile(file));
        // resets the value of file input so the same file can be uploaded again (onChange wouldn't fire for same file)
        const fileInput = event.target as HTMLInputElement;
        fileInput.value = '';
    }

    onFileDropped(fileList: FileList) {
        Array.from(fileList).forEach(file => this.uploadFile(file));
    }

    openFile(documentName: string) {
        this.worklistService.getFileUrl(this.worklistItem.id, documentName).then(fileUrl => window.open(fileUrl, '_blank'));
    }

    private uploadFile(fileToUpload: File) {
        if (fileToUpload) {
            const fileInfo = FileUploadInfo.generateFileUploadInfo(fileToUpload);
            if (this.isFileValid(fileToUpload, fileInfo)) {
                this.fileUploadInfoList.push(fileInfo);
                const newTask = new FileUploadTask(fileToUpload, fileInfo);
                this.addTaskToQueue(newTask);
            }
        }
    }

    private isFileValid(uploadedFile: File, fileInfo: FileUploadInfo): boolean {
        if (!ScanDocsComponent.acceptedFileMimeType.find(fileType => fileType === uploadedFile.type)) {
            this.promptErrorMessage(fileInfo, FileUploadInfoErrorEnum.fileType);
        } else if (uploadedFile.size > ScanDocsComponent.maxFileSize) {
            this.promptErrorMessage(fileInfo, FileUploadInfoErrorEnum.fileSize);
        } else {
            return true;
        }
        fileInfo.cancelUpload();
        return false;
    }

    private addTaskToQueue(uploadTask: FileUploadTask) {
        this._fileUploadTaskQueue.push(uploadTask);
        while (this._fileUploadTaskQueue.length && this._fileUploadProcessingList.length < ScanDocsComponent.maxConcurrentTask) {
            this.processNextTask();
        }
    }

    cancelUpload(fileInfo: FileUploadInfo) {
        if (fileInfo && !fileInfo.isSuccess) {
            fileInfo.cancelUpload();
            this.fileUploadInfoList = this.fileUploadInfoList.filter(file => file !== fileInfo);
            this._fileUploadTaskQueue = this._fileUploadTaskQueue.filter(task => task.fileUploadInfo !== fileInfo);
            this._fileUploadProcessingList = this._fileUploadProcessingList.filter(task => task.fileUploadInfo !== fileInfo);

            this.processNextTask();
        }
    }

    private processNextTask() {
        if (this._fileUploadTaskQueue.length && this._fileUploadProcessingList.length < ScanDocsComponent.maxConcurrentTask) {
            const currentUploadTask = this._fileUploadTaskQueue.shift();
            this._fileUploadProcessingList.push(currentUploadTask);
            this.processNewTask(currentUploadTask);
        }
    }

    private processNewTask(newUploadTask: FileUploadTask) {
        const formData = new FormData();
        formData.append(ScanDocsComponent.formDataFileKey, newUploadTask.file);
        newUploadTask.fileUploadInfo.markAsUploading();

        const uploadSubscription = this.worklistService
            .uploadScanDoc(this.worklistItem.id, formData)
            .pipe(catchError(this.getErrorMessage))
            .subscribe({
                next: event => {
                    if (event.type === HttpEventType.UploadProgress && event.total) {
                        newUploadTask.fileUploadInfo.updateProgress(event.loaded);
                        if (event.loaded === event.total) {
                            newUploadTask.fileUploadInfo.markAsInProgress();
                        }
                    } else if (event.type === HttpEventType.Response && event.status === HttpStatusCode.Accepted) {
                        newUploadTask.fileUploadInfo.markAsSuccess();

                        this._fileUploadProcessingList = this._fileUploadProcessingList.filter(task => task !== newUploadTask);
                        this.processNextTask();
                    }
                },
                error: (err: Error) => {
                    this.promptErrorMessage(newUploadTask.fileUploadInfo, FileUploadInfoErrorEnum.onUpload, err.message);
                    this.cancelUpload(newUploadTask.fileUploadInfo);
                }
            });
        // overall subscriptions to unsubscribe when component gets destroyed
        this.subscriptions.add(uploadSubscription);
        // individual subscription to cancel the upload
        newUploadTask.fileUploadInfo.updateSubscription(uploadSubscription);
    }

    private promptErrorMessage(fileInfo: FileUploadInfo, errorType: FileUploadInfoErrorEnum, errorMessage?: string) {
        const title = 'Scanned Document Upload Error';
        let message;
        const information = `The file "${fileInfo.name}" has failed to upload.`;

        switch (errorType) {
            case 'onUpload':
                message = errorMessage ?? 'The file could not be uploaded, please try to upload again in a few minutes.';
                break;
            case 'fileSize': {
                const sizeLimitMB = CommonUtilities.bytesToMegabytes(ScanDocsComponent.maxFileSize);
                message = errorMessage ?? `Only files less than ${sizeLimitMB}MB in size can be attached.`;
                break;
            }
            case 'fileType':
                message =
                    errorMessage ??
                    `The file is in ${fileInfo.extension} format. Only ${ScanDocsComponent.acceptedFileExtensionsStr} files can be attached.`;
                break;
            default:
                return;
        }

        this.subscriptions.add(
            this.messageBoxService
                .showMessageBox({
                    title,
                    message,
                    information,
                    icon: MessageBoxIcon.Confirmation,
                    buttons: MessageBoxButtons.Ok,
                    allowMultiplePopup: true
                })
                .subscribe()
        );
    }

    private getErrorMessage(response: HttpErrorResponse | TimeoutError) {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent || response.status === 502 || response.status === 503 || response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        }
        console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
        if (response.status === 403) {
            return throwError(() => new Error('You do not have permission to upload the documents'));
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                response.error?.errors?.[ScanDocsComponent.formDataFileKey]?.toLocaleString() ?? ScanDocsComponent.UNABLE_TO_UPLOAD_MESSAGE;
            return throwError(() => new Error(errorMessage));
        }
        return throwError(() => new Error(ScanDocsComponent.UNABLE_TO_UPLOAD_MESSAGE));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}

class FileUploadTask {
    file: File;
    fileUploadInfo: FileUploadInfo;

    constructor(file: File, fileUploadInfo: FileUploadInfo) {
        this.file = file;
        this.fileUploadInfo = fileUploadInfo;
    }
}
